// composables/useVerification.ts
import { ref, onUnmounted, computed } from 'vue';
import { i18n } from '@/plugins/vue-i18n';
import { SessionVerificationResource } from '@clerk/types';
import clerk, {
  getMessageKeyByClerkError,
  getClerkErrorCode,
} from '@/lib/clerk';
import router from '@/router';
import { useCompanyFeatureComposable } from '@/composables/useCompanyFeatures';
import sharedMessages from '@/lib/shared-messages';
import { generalApi } from '@/lib/api/general';

type secondFactors = 'totp' | 'phone_code' | 'backup_code' | 'email_code';
// `first_factor_email_code` re-verifies a session with a mailed code, for
// SAML users who may have no password. Not the Client Trust `email_code`.
type firstFactors = 'password' | 'first_factor_email_code';

// Strategies we render a UI for. `email_code` covers Clerk Client Trust
// (email challenge on a new/untrusted device); `email_link` is intentionally
// excluded since we don't offer a magic-link UI.
const SUPPORTED_SECOND_FACTORS: secondFactors[] = [
  'totp',
  'phone_code',
  'backup_code',
  'email_code',
];

// clerk-js 5.127 returns an `email_code` second factor for Client Trust, but the
// transitively-resolved `@clerk/types` version can narrow the strategy union and
// omit it. So we match the strategy as a plain string and cast the SDK params
// that are typed against the narrower union. Safe: `email_code` is a valid
// strategy at runtime regardless of the resolved type declarations.
interface EmailCodeFactorLike {
  strategy: 'email_code';
  emailAddressId: string;
  safeIdentifier: string;
}
const findEmailCodeFactor = (
  factors: { strategy: string }[] | null | undefined
): EmailCodeFactorLike | undefined =>
  factors?.find((f) => f.strategy === 'email_code') as
    | EmailCodeFactorLike
    | undefined;

export interface VerificationOptions {
  isDialog?: boolean;
  onSuccess?: (code?: string) => void;
  preferredMode?: 'totp' | 'phone_code';
}

export function useVerification(options: VerificationOptions = {}) {
  const $t = i18n.tc.bind(i18n);
  const { isDialog = false, onSuccess, preferredMode = 'totp' } = options;
  const { isIdpInitiatedEnabled } = useCompanyFeatureComposable();

  // Reactive state
  const availableMethods = ref<secondFactors[] | undefined>(undefined);
  const mode = ref<secondFactors | firstFactors | undefined>(undefined);
  const verificationToken = ref<string>('');
  const isInitializing = ref<boolean>(true);
  const isVerifying = ref<boolean>(false);
  const errorAlert = ref<string>('');
  const waitForCooldown = ref<boolean>(false);
  const resendingCode = ref<boolean>(false);
  const resendRequired = ref<boolean>(false);
  const codeResent = ref<boolean>(false);
  const cooldownInterval = ref<NodeJS.Timeout | null>(null);
  const cooldownTimeLeft = ref<number>(30);
  const tfaPhoneNumber = ref<string>('');
  const tfaEmail = ref<string>('');
  const emailAuthEnabled = ref<boolean>(false);
  const codeSent = ref<boolean>(false);
  const clerkSessionVerification = ref<SessionVerificationResource | null>(
    null
  );

  // Constants
  const RESEND_DELAY = 30000;

  // Computed properties
  const showAlternatives = computed(() => {
    const methodsExceptBackup = availableMethods.value?.filter(
      (m) => m !== 'backup_code'
    );
    return methodsExceptBackup && methodsExceptBackup.length > 1;
  });

  const canUseBackupCodes = computed(() => {
    return (
      availableMethods.value?.includes('totp') &&
      availableMethods.value?.includes('backup_code')
    );
  });

  // Methods
  const clearCooldown = () => {
    if (cooldownInterval.value) {
      clearInterval(cooldownInterval.value);
      cooldownInterval.value = null;
    }
  };

  const startResendCooldown = () => {
    waitForCooldown.value = true;
    cooldownTimeLeft.value = RESEND_DELAY / 1000;

    cooldownInterval.value = setInterval(() => {
      cooldownTimeLeft.value -= 1;
      if (cooldownTimeLeft.value <= 0) {
        waitForCooldown.value = false;
        clearCooldown();
      }
    }, 1000);
  };

  const switchMode = async (
    newMode: secondFactors | firstFactors | 'default'
  ) => {
    resetForm();
    if (newMode === 'default') {
      newMode = availableMethods.value?.[0] || 'totp';
    }
    if (
      (newMode === 'phone_code' ||
        newMode === 'email_code' ||
        newMode === 'first_factor_email_code') &&
      !codeSent.value
    ) {
      try {
        await sendCode(newMode);
      } catch (error) {
        const err = error instanceof Error ? error : new Error('Unknown error');
        errorAlert.value = getMessageKeyByClerkError(err);
      }
    }
    mode.value = newMode;
  };

  const resetForm = () => {
    verificationToken.value = '';
    errorAlert.value = '';
    codeResent.value = false;
    resendRequired.value = false;
    isVerifying.value = false;
    clearCooldown();
  };

  const initializeVerification = async () => {
    if (!clerk) {
      throw new Error('Clerk is not initialized.');
    }
    isInitializing.value = true;
    if (isDialog) {
      if (!clerk.session) {
        throw new Error('Clerk session is not available.');
      }
      if (!clerkSessionVerification.value) {
        clerkSessionVerification.value = await clerk.session.startVerification({
          level: 'second_factor',
        });
      }
      const res = clerkSessionVerification.value;
      const supportsSecondFactor =
        res.supportedSecondFactors instanceof Array &&
        res.supportedSecondFactors.length > 0;
      if (supportsSecondFactor) {
        availableMethods.value = res?.supportedSecondFactors?.map(
          (sf) => sf.strategy
        );
      }
    } else if (
      clerk.client?.signIn &&
      clerk.client.signIn.supportedSecondFactors
    ) {
      const signIn = clerk.client.signIn;

      // Check if sign-in is in correct state
      const firstFactorCompleteAt = clerk.client.updatedAt;
      const secondFactorExpireAt = firstFactorCompleteAt
        ? new Date(firstFactorCompleteAt.getTime() + 10 * 60 * 1000)
        : null;

      if (
        signIn.status !== 'needs_second_factor' ||
        !signIn.supportedSecondFactors ||
        !signIn.supportedSecondFactors.length ||
        (secondFactorExpireAt && secondFactorExpireAt < new Date())
      ) {
        // Keep the current query — notably `redirect` — so the login the
        // user is about to redo still returns them where they came from.
        // That destination lives only in the URL (see `ensureLoggedOut`).
        router.push({ name: 'login', query: { ...router.currentRoute.query } });
        return;
      }

      // Set phone number if available
      const phoneNumberFactor = signIn.supportedSecondFactors.find(
        (sf) => sf.strategy === 'phone_code'
      );
      if (phoneNumberFactor) {
        tfaPhoneNumber.value = phoneNumberFactor.safeIdentifier;
      }

      // Set the (masked) email if a Client Trust email challenge is offered.
      const emailCodeFactor = findEmailCodeFactor(
        signIn.supportedSecondFactors
      );
      if (emailCodeFactor) {
        tfaEmail.value = emailCodeFactor.safeIdentifier;

        // Users who enabled email authentication get this challenge on every
        // login, not only from a new device, so the wording differs. The masked
        // `safeIdentifier` can't be used for the lookup, but the sign-in keeps
        // the identifier the user typed (also across page reloads).
        // Fail-open: on error we keep the new-device wording.
        if (signIn.identifier) {
          try {
            const status = await generalApi.getEmail2faStatus(
              signIn.identifier
            );
            emailAuthEnabled.value = !!status.data?.email_2fa_enabled;
          } catch (error) {
            console.error('Failed to fetch email 2FA status:', error);
          }
        }
      }

      // Use the signIn object instead to get available methods, keeping only
      // the strategies we render a UI for.
      availableMethods.value = clerk.client.signIn.supportedSecondFactors
        .map((sf) => sf.strategy as secondFactors)
        .filter((s) => SUPPORTED_SECOND_FACTORS.includes(s));
    } else {
      // Something isn't right, no methods available. Error out.
      errorAlert.value = sharedMessages.genericError;
      throw new Error('No available second factor methods found.');
    }

    // If we're verifying the second factor
    if (
      (isDialog &&
        clerkSessionVerification?.value?.status === 'needs_second_factor') ||
      (!isDialog && clerk.client?.signIn?.status === 'needs_second_factor')
    ) {
      if (!availableMethods.value || availableMethods.value?.length === 0) {
        errorAlert.value = sharedMessages.genericError;
        throw new Error('No available second factor methods found.');
      }

      if (availableMethods.value.includes(preferredMode)) {
        await switchMode(preferredMode);
      } else if (availableMethods.value.includes('totp')) {
        await switchMode('totp');
      } else if (availableMethods.value.includes('phone_code')) {
        if (!isDialog) {
          // Assume that the code is sent, as during login, it is sent as part of the
          // login action. This is to avoid spamming verifiation codes via page reloads.
          codeSent.value = true;
        }
        await switchMode('phone_code');
      } else if (availableMethods.value.includes('email_code')) {
        // Clerk Client Trust email challenge (new/untrusted device). As with
        // phone_code, the code is sent by the login action, so assume it's sent
        // to avoid re-sending (and re-emailing) on page reloads.
        if (!isDialog) {
          codeSent.value = true;
        }
        await switchMode('email_code');
      } else if (availableMethods.value.length > 0) {
        await switchMode(availableMethods.value[1]);
      } else {
        errorAlert.value = sharedMessages.genericError;
        throw new Error('Could not determine appropriate verification method.');
      }
    } else if (
      isDialog &&
      clerkSessionVerification.value?.status === 'needs_first_factor'
    ) {
      // If we're verifying the first factor
      const firstFactors = clerkSessionVerification.value.supportedFirstFactors;
      const emailFactor = findEmailCodeFactor(firstFactors);
      const hasPassword = !!firstFactors?.some(
        (f) => f.strategy === 'password'
      );

      // Only SAML tenants that allow IdP-Initiated login can set up 2FA, and
      // their users may have no password, so mail a code instead.
      if (emailFactor && (isIdpInitiatedEnabled.value || !hasPassword)) {
        tfaEmail.value = emailFactor.safeIdentifier;
        await switchMode('first_factor_email_code');
      } else {
        await switchMode('password');
      }
    } else {
      // We should not end up here. Error out.
      errorAlert.value = sharedMessages.genericError;
      throw new Error('Could not determine appropriate verification method.');
    }

    isInitializing.value = false;
  };

  const sendCode = async (sendMode = mode.value) => {
    if (!clerk) {
      return;
    }
    if (isDialog && clerk.session && sendMode === 'first_factor_email_code') {
      const emailFactor = findEmailCodeFactor(
        clerkSessionVerification.value?.supportedFirstFactors
      );
      if (!emailFactor) {
        errorAlert.value = sharedMessages.genericError;
        return false;
      }
      await clerk.session.prepareFirstFactorVerification({
        strategy: 'email_code',
        emailAddressId: emailFactor.emailAddressId,
      } as unknown as Parameters<typeof clerk.session.prepareFirstFactorVerification>[0]);
    } else if (
      isDialog &&
      clerk.session &&
      clerk.session.prepareSecondFactorVerification &&
      clerk.user
    ) {
      // For dialog (session-based)
      await clerk.session.prepareSecondFactorVerification({
        strategy: 'phone_code',
        phoneNumberId: clerk.user.phoneNumbers.find(
          (pn) => pn.reservedForSecondFactor
        )?.id,
      });
    } else if (clerk.client?.signIn) {
      // For login page (sign-in based)
      const signIn = clerk.client.signIn;
      const phoneNumberFactor = signIn.supportedSecondFactors?.find(
        (sf) => sf.strategy === 'phone_code'
      );
      const emailCodeFactor = findEmailCodeFactor(
        signIn.supportedSecondFactors
      );

      if (phoneNumberFactor) {
        tfaPhoneNumber.value = phoneNumberFactor.safeIdentifier;
        await signIn.prepareSecondFactor({
          strategy: 'phone_code',
          phoneNumberId: phoneNumberFactor.phoneNumberId,
        });
      } else if (emailCodeFactor) {
        // Clerk Client Trust: send the email verification code.
        tfaEmail.value = emailCodeFactor.safeIdentifier;
        await signIn.prepareSecondFactor({
          strategy: 'email_code',
          emailAddressId: emailCodeFactor.emailAddressId,
        } as unknown as Parameters<typeof signIn.prepareSecondFactor>[0]);
      }
    } else {
      errorAlert.value = sharedMessages.genericError;
      console.error('No valid Clerk session or sign-in found.');
      return false;
    }
    codeSent.value = true;
    return true;
  };

  const resendCode = async () => {
    if (waitForCooldown.value) return;

    verificationToken.value = '';
    errorAlert.value = '';
    resendingCode.value = true;
    codeResent.value = false;

    startResendCooldown();

    try {
      const resendSuccess = await sendCode();
      codeResent.value = !!resendSuccess;
    } catch (error) {
      const err = error instanceof Error ? error : new Error('Unknown error');
      const errorMessage = getMessageKeyByClerkError(err);
      errorAlert.value = errorMessage;
    } finally {
      resendingCode.value = false;
      resendRequired.value = false;
    }
  };

  const verifyCode = async () => {
    if (resendRequired.value || mode.value === undefined || !clerk.client)
      return;

    isVerifying.value = true;
    codeResent.value = false;
    errorAlert.value = '';

    try {
      if (
        isDialog &&
        clerk.session &&
        clerk.session.attemptSecondFactorVerification
      ) {
        if (mode.value === 'first_factor_email_code') {
          await clerk.session.attemptFirstFactorVerification({
            strategy: 'email_code',
            code: verificationToken.value,
          } as unknown as Parameters<typeof clerk.session.attemptFirstFactorVerification>[0]);
        } else if (mode.value !== 'password' && mode.value !== 'email_code') {
          // For dialog (session-based). Note: email_code is a login-only
          // Client Trust factor and is never used for session reverification.
          await clerk.session.attemptSecondFactorVerification({
            strategy: mode.value,
            code: verificationToken.value,
          });
        } else {
          await clerk.session.attemptFirstFactorVerification({
            strategy: 'password',
            password: verificationToken.value,
          });
        }
      } else if (clerk.client.signIn) {
        // For login page (sign-in based)
        if (mode.value === 'password') {
          // Invalid state. Password should not be used here.
          throw new Error(
            'Password cannot be used for second factor verification.'
          );
        }
        const signIn = await clerk.client.signIn.attemptSecondFactor({
          strategy: mode.value,
          code: verificationToken.value,
        } as unknown as Parameters<typeof clerk.client.signIn.attemptSecondFactor>[0]);

        if (signIn.status === 'complete') {
          await clerk.setActive({ session: signIn.createdSessionId });
          window.location.reload();
          return;
        } else {
          throw new Error('Unknown error');
        }
      } else {
        // Something isn't right, no methods available. Error out.
        throw new Error('No valid Clerk session or sign-in found.');
      }

      // Remove the session verification object so that subsequent calls
      // start a fresh verification process with updated methods.
      clerkSessionVerification.value = null;

      // The SMS code is now either consumed or invalidated.
      // Hence, we need to reset the codeSent state so a new
      // code can be sent in subsequent verifications.
      codeSent.value = false;

      onSuccess?.(verificationToken.value);
    } catch (error) {
      console.error('Verification failed:', error);
      const err = error instanceof Error ? error : new Error('Unknown error');

      // 失敗ログインを監査に記録（fire-and-forget・best-effort）。
      // 対象はログイン中の2要素認証(!isDialog)のみ。ログイン済みユーザーの
      // セッション再認証(isDialog)は「ログイン失敗」ではないため除外する。
      // Clerk のエラーコード(form_code_incorrect / verification_failed 等)を
      // そのまま送り、backend の _FAILURE_CODES に一致するものだけ記録される。
      const loginIdentifier = clerk.client?.signIn?.identifier;
      if (!isDialog && loginIdentifier) {
        void generalApi.reportLoginFailed(
          loginIdentifier,
          getClerkErrorCode(err)
        );
      }

      if (getClerkErrorCode(err) === 'form_password_incorrect') {
        errorAlert.value = $t(sharedMessages.incorrectPassword);
      } else {
        errorAlert.value = getMessageKeyByClerkError(err);
      }
      isVerifying.value = false;
    }
  };

  const updateVerificationToken = (value: string) => {
    verificationToken.value = value;
  };

  // Cleanup
  onUnmounted(() => {
    clearCooldown();
  });

  return {
    // State
    mode,
    verificationToken,
    isInitializing,
    isVerifying,
    errorAlert,
    waitForCooldown,
    resendingCode,
    resendRequired,
    codeResent,
    cooldownTimeLeft,
    tfaPhoneNumber,
    tfaEmail,
    emailAuthEnabled,

    // Computed
    showAlternatives,
    canUseBackupCodes,

    // Methods
    verifyCode,
    resendCode,
    switchMode,
    updateVerificationToken,
    resetForm,
    initializeVerification,

    // Constants
    RESEND_DELAY,
  };
}
